import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_groupCourseSessionMutation,
  useGET_groupCourseSessionByIdQuery,
  usePATCH_groupCourseSessionMutation,
  usePOST_groupCourseSessionMutation,
} from 'redux/api/sessionsApi'

import { useGET_courseChapterSelectQuery } from 'redux/api/coursesApi'

import { useGET_lessonPlansSelectQuery } from 'redux/api/lessonPlansApi'
import { useGET_groupCourseByIdQuery, useGET_groupCourseSelectQuery } from 'redux/api/groupCoursesApi'
import { useGET_academicPeriodsSelectQuery } from 'redux/api/academicPeriodsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/sessionModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType, ISessionData } from 'types'
import { useSelector } from 'react-redux'
import {  RootState } from 'redux/store'
import { useGET_studentGroupsQuery } from 'redux/api/groupsApi'
import DatePickerField from 'components/atoms/fields/DatePickerField'


interface IProps {
  action: ActionType
  id: string
  group_course_id: string
  viewOnly: boolean
}

const SessionForm: FC<IProps> = ({ action, id, group_course_id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backUrl = useSelector((state: RootState) => state['sessions'].sessionBackUrl)

  //const backToSlug = backUrl || (group_course_id ? `/group-courses/view/${group_course_id}` : `/sessions/`)

  const backToSlug = '/sessions'

  const editSlug =  group_course_id ? `/group-courses/edit/${group_course_id}` : `/sessions/edit/${id}`


  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.sessions')}`,
    entityId: id,
    getEntityQuery: useGET_groupCourseSessionByIdQuery,
    createEntityMutation: usePOST_groupCourseSessionMutation,
    editEntityMutation: usePATCH_groupCourseSessionMutation,
    deleteEntityMutation: useDELETE_groupCourseSessionMutation,
    
    onSuccess: () => {
      window.location.replace(backToSlug)
    }
  })

  const { 
    group,
    group_course, 
    group_course_session_plan,
    start_datetime, 
    start_time,
    duration_minutes,
    location
  } = formModel()

  const serverValues = entity?.id ? entity : null

  const { data: selected_group_course } = useGET_groupCourseByIdQuery(group_course_id, { skip: action !== 'add' || !group_course_id || serverValues})


  let formValues = serverValues || initialValues

  if(selected_group_course){
    formValues.group_course = {
      id: selected_group_course.id, 
      title: `${selected_group_course.group?.title} / ${selected_group_course.course?.title}`
    }
    formValues.group_course_id = selected_group_course.id
  }


  return (
    <Formik
      enableReinitialize
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: ISessionData = {
          ...values,
          group_course_id: values.group_course?.id ?? null,
          group_course_session_plan_id: values.group_course_session_plan?.id ?? null,
        }

        handleSubmit(action, id, data)
      }}
    >{({ values, setFieldValue, errors }) => (
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem', marginBottom: '2rem' }}>

            <Grid item xs={12} md={3}>
              <SelectField
                 fieldProps={{ ...group }}
                 apiCall={useGET_studentGroupsQuery}
                    onChange={() => {
                      setFieldValue('student', null)
                    }}
                />
            </Grid>

            <Grid item xs={12} md={3}>
              <SelectField
                fieldProps={{ ...group_course }}
                apiCall={useGET_groupCourseSelectQuery}
                disabled={ (viewOnly || serverValues !== null || selected_group_course) ? true : false }
              />
            </Grid>

            <Grid item xs={12} lg={3}>
              <SelectField
                fieldProps={{ ...group_course_session_plan }}
                apiCall={useGET_lessonPlansSelectQuery}
                filters={{subject: values?.group_course?.subject_id ?? ''}}
                disabled={viewOnly}
              />
            </Grid>

            <Grid item xs={12} lg={3}>
              <InputField fieldProps={{ ...location }} disabled={viewOnly} />
            </Grid>



            <Grid item xs={12} lg={3}>
              <DatePickerField fieldProps={{ ...start_datetime }} disabled={viewOnly}/>
            </Grid>

            <Grid item xs={12} lg={3}>
              <InputField fieldProps={{ ...duration_minutes }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} lg={3}>
            </Grid>

            <Grid item xs={12} lg={3}>
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Grid>
          </Grid>          


          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              
              {action !== 'add' &&
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>}

              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              
            </Box>
          </Grid>

      </Form>
      )}
    </Formik>
  )
}
export default SessionForm
